import { useState, useEffect } from "react";

interface MobileDetectionResult {
  isMobile: boolean;
  isIOS: boolean;
  isIOS26: boolean;
  viewportHeight: number;
  safeAreaInsets: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

/**
 * Hook personalizado para detectar dispositivos móviles
 * Optimizado para iOS 26 con detección de Liquid Glass UI y safe-area-insets
 */
export const useIsMobile = (): MobileDetectionResult => {
  const [mobileState, setMobileState] = useState<MobileDetectionResult>(() => {
    // Estado inicial seguro para SSR
    if (typeof window === "undefined") {
      return {
        isMobile: false,
        isIOS: false,
        isIOS26: false,
        viewportHeight: 0,
        safeAreaInsets: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        },
      };
    }

    return {
      isMobile: false,
      isIOS: false,
      isIOS26: false,
      viewportHeight: window.innerHeight,
      safeAreaInsets: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
    };
  });

  useEffect(() => {
    const detectMobile = () => {
      // Detectar móvil por user agent y viewport
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA =
        /iphone|ipod|android|blackberry|windows phone|opera mini|mobile/i.test(
          userAgent
        );
      const isMobileViewport = window.innerWidth <= 768;
      const isMobile = isMobileUA || isMobileViewport;

      // Detectar iOS
      const isIOS =
        /iphone|ipod|ipad/i.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      // Detectar iOS 26 específicamente
      const iosVersion = getIOSVersion();
      const isIOS26 = isIOS && iosVersion >= 26;

      // Calcular safe-area-insets para iOS 26 Liquid Glass UI
      const safeAreaInsets = getSafeAreaInsets();

      // Viewport height real (considerando barras de Safari en iOS 26)
      const viewportHeight = getActualViewportHeight();

      setMobileState({
        isMobile,
        isIOS,
        isIOS26,
        viewportHeight,
        safeAreaInsets,
      });
    };

    // Ejecutar al montar
    detectMobile();

    // Redetectar en resize y orientationchange
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        detectMobile();
      }, 150); // Debounce para evitar sobrecarga
    };

    const handleOrientationChange = () => {
      // iOS 26 necesita delay después de orientationchange
      setTimeout(detectMobile, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    // Limpiar listeners
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return mobileState;
};

/**
 * Obtener versión de iOS del user agent
 */
function getIOSVersion(): number {
  const match = navigator.userAgent.match(/OS (\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return 0;
}

/**
 * Obtener safe-area-insets usando CSS env()
 * Crítico para iOS 26 Liquid Glass UI
 */
function getSafeAreaInsets() {
  const getComputedInset = (name: string): number => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`env(safe-area-inset-${name})`)
      .trim();
    return parseInt(value, 10) || 0;
  };

  return {
    top: getComputedInset("top"),
    bottom: getComputedInset("bottom"),
    left: getComputedInset("left"),
    right: getComputedInset("right"),
  };
}

/**
 * Obtener viewport height real
 * En iOS 26, window.innerHeight no es confiable debido a Liquid Glass UI
 */
function getActualViewportHeight(): number {
  // Intentar usar visualViewport API primero (más preciso en iOS 26)
  if (window.visualViewport) {
    return window.visualViewport.height;
  }

  // Fallback a innerHeight
  return window.innerHeight;
}

/**
 * Hook adicional para setear CSS custom properties
 * Útil para 100vh fixes en iOS 26
 */
export const useViewportHeight = () => {
  useEffect(() => {
    // Verificar que estamos en el navegador
    if (typeof window === "undefined") return;

    const updateHeight = () => {
      // Calcular 1vh real
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);

      // También setear viewport height completo
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${window.innerHeight}px`
      );

      // Log para debugging (solo en desarrollo)
      if (process.env.NODE_ENV === "development") {
      }
    };

    // Ejecutar inmediatamente
    updateHeight();

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeight, 100);
    };

    const handleOrientationChange = () => {
      setTimeout(updateHeight, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    // iOS Safari cambia height al scrollear - pero con debounce para no saturar
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateHeight, 200);
    };
    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("scroll", handleScroll);
      clearTimeout(resizeTimeout);
      clearTimeout(scrollTimeout);
    };
  }, []);
};
